import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [entityId, setEntityId] = useState(null);

  useEffect(() => {
    // Load cart from localStorage
    const storedCart = localStorage.getItem('ruchibox_cart');
    const storedEntity = localStorage.getItem('ruchibox_cart_entity');
    if (storedCart) {
      try {
        setItems(JSON.parse(storedCart));
      } catch (e) {
        localStorage.removeItem('ruchibox_cart');
      }
    }
    if (storedEntity) {
      setEntityId(storedEntity);
    }
  }, []);

  const saveCart = (newItems) => {
    setItems(newItems);
    localStorage.setItem('ruchibox_cart', JSON.stringify(newItems));
  };

  const addItem = (item, quantity = 1) => {
    const existingIndex = items.findIndex(i => i.id === item.id);
    let newItems;
    
    if (existingIndex >= 0) {
      newItems = items.map((i, idx) => 
        idx === existingIndex 
          ? { ...i, quantity: i.quantity + quantity }
          : i
      );
    } else {
      newItems = [...items, { ...item, quantity }];
    }
    
    saveCart(newItems);
  };

  const removeItem = (itemId) => {
    const newItems = items.filter(i => i.id !== itemId);
    saveCart(newItems);
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeItem(itemId);
      return;
    }
    const newItems = items.map(i => 
      i.id === itemId ? { ...i, quantity } : i
    );
    saveCart(newItems);
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem('ruchibox_cart');
  };

  const setCartEntity = (id) => {
    if (entityId && entityId !== id && items.length > 0) {
      // Clear cart if changing entity
      clearCart();
    }
    setEntityId(id);
    localStorage.setItem('ruchibox_cart_entity', id);
  };

  const getTotal = () => {
    return items.reduce((total, item) => {
      const price = item.is_free ? 0 : item.price;
      return total + (price * item.quantity);
    }, 0);
  };

  const getItemCount = () => {
    return items.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      items,
      entityId,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      setCartEntity,
      getTotal,
      getItemCount
    }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
